/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Tag, Typography, breakpoints } from '@coursera/cds-core';
import { SuccessIcon } from '@coursera/cds-icons';

import CompletionWarningRenderer from 'bundles/degree-home/components/v3-dpm/components/CompletionWarningRenderer';
import type { UseCurriculum_DegreeCategoryFragment as CategoryGql } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCurriculum';
import useProgressionCalculator from 'bundles/degree-home/components/v3-dpm/lib/useProgressionCalculator';
import { MetadataTypography, joinMetadataStrings } from 'bundles/program-loggedin-shared/components/Metadata';
import { useCategoryCompletionRule } from 'bundles/program-loggedin-shared/hooks/completionRules';
import { renderGpa } from 'bundles/program-loggedin-shared/libraries/gradeUtils';

import _t from 'i18n!nls/degree-home';

interface Props {
  category: CategoryGql;
}

const styles = {
  categoryHeaderContainer: () => css`
    margin-bottom: var(--cds-spacing-300);
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,
  categoryHeaderContent: css`
    width: fit-content;
  `,
  completeTagContainer: () => css`
    ${breakpoints.down('xs')} {
      padding-top: var(--cds-spacing-300);
    }
  `,
};

const CategoryHeader = ({ category }: Props) => {
  const { title, description, completionRule, minGpa, minPassingGradeForCourses } = category;
  const { minUnits } = completionRule;

  const overallProgression = useProgressionCalculator();
  const categoryProgression = overallProgression?.categories?.[category.id];
  const hasGradeWarning = categoryProgression?.warnings?.grade;
  const hasGPAWarning = categoryProgression?.warnings?.gpa;
  const categoryGPA = categoryProgression?.categoryGPA;
  const isCategoryCompleted = categoryProgression?.completionRuleMet && !hasGPAWarning && !hasGradeWarning;

  const completionRuleText = useCategoryCompletionRule(minUnits);

  const metadataStrings = [completionRuleText];
  if (typeof minGpa === 'number') {
    metadataStrings.push(_t('Min GPA: #{gpa}', { gpa: renderGpa(minGpa) }));
  }

  if (typeof minPassingGradeForCourses === 'number') {
    metadataStrings.push(_t('Min Grade: #{grade}%', { grade: minPassingGradeForCourses }));
  }

  return (
    <div css={styles.categoryHeaderContainer}>
      <div css={styles.categoryHeaderContent}>
        {title && (
          <Typography variant="h2semibold" component="h3">
            {title}
          </Typography>
        )}
        <MetadataTypography>{joinMetadataStrings(metadataStrings)}</MetadataTypography>
        {description && <MetadataTypography>{description}</MetadataTypography>}
        <CompletionWarningRenderer
          level="category"
          hasGradeWarning={hasGradeWarning}
          hasGPAWarning={hasGPAWarning}
          gpa={categoryGPA}
        />
      </div>
      {isCategoryCompleted && (
        <div css={styles.completeTagContainer}>
          <Tag
            data-testid="complete-tag-category-level"
            variant="status"
            priority="primary"
            color="success"
            icon={<SuccessIcon />}
          >
            {_t('Complete')}
          </Tag>
        </div>
      )}
    </div>
  );
};

export default CategoryHeader;
